import { get, isNil, isObject } from 'lodash-es';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  DropzoneAreaInput,
  FormDropzoneAreaProps,
  PreviewList,
  FormFeedback,
  FormGroup,
  Icon,
  Label,
  Spinner,
  Tooltip,
  notify,
  useConfiguredForm,
} from '../index';

import { IResource, ResourceType } from '@wyz/types';
import clsx from 'clsx';
import './dropzone-area-field.scss';
import { IDropzoneContext } from '../dropzone-area-input/context';

type PrivateProps<TKeys extends string> = Omit<
  FormDropzoneAreaProps,
  | 'dropzoneText'
  | 'onChange'
  | 'initialFiles'
  | 'inputProps'
  | 'onDelete'
  | 'onAlert'
> &
  Pick<IDropzoneContext, 'translations'> & {
    isUploading?: boolean;
    translate: (key: TKeys) => string;
  } & {
    tooltips?: React.ReactNode;
    resourceType?: ResourceType;
    readOnly?: boolean;
  };

const DropzoneAreaField = <TKeys extends string>(
  props: PrivateProps<TKeys>,
) => {
  const {
    name,
    label,
    helperText,
    defaultValues,
    acceptedFiles = 'IMAGE',
    filesLimit,
    resourceReader,
    createFunction,
    isUploading = false,
    required,
    translate,
    translations,
    tooltips,
    resourceType,
    ...rest
  } = props;
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();
  const configurationContext = useConfiguredForm();

  const initialValue = defaultValues || getValues(name);
  const errorMessage = get(errors, [
    ...props.name.split('.'),
    'message',
  ]) as unknown as TKeys;
  let initialFiles: Array<{
    file: File;
    resource: IResource;
  }> = [];
  if (initialValue) {
    initialFiles =
      filesLimit === 1
        ? [
            {
              resource:
                isObject(initialValue) && 'id' in initialValue
                  ? initialValue
                  : undefined,
              file: initialValue instanceof File ? initialValue : undefined,
            },
          ]
        : initialValue.map((resource: IResource) => ({
            file: new File([], resource.originalFileName || '', {
              type: resource.mimeType || '',
            }),
            resource,
          }));
  }
  return (
    <FormGroup>
      {label && (
        <div className='space-x-2 position-relative'>
          {tooltips && (
            <div className='position-absolute'>
              <Tooltip placement='right' label={tooltips}>
                <Icon icon={'info-circle'} width={13} height={13} />
              </Tooltip>
            </div>
          )}
          <Label
            htmlFor={props.id || props.name}
            className={clsx({
              'ms-3': tooltips,
              'required': props.required,
            })}
          >
            {label}
          </Label>
        </div>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue={initialValue}
        shouldUnregister={false}
        render={({ field }) => {
          if (configurationContext.mode === 'WRITE' && !props.readOnly)
            return (
              <div className='position-relative'>
                {isUploading && (
                  <div className='transform-center'>
                    <Spinner />
                  </div>
                )}
                <DropzoneAreaInput
                  {...rest}
                  showPreviews
                  translations={translations}
                  acceptedFiles={acceptedFiles}
                  dropzoneText={label}
                  onChange={async (files) => {
                    try {
                      if (resourceType && !isNil(createFunction)) {
                        const resources = await createFunction(
                          files,
                          resourceType,
                        );
                        const nextValue =
                          filesLimit === 1 ? resources[0] : resources;
                        field.onChange(nextValue);
                      } else {
                        field.onChange(filesLimit === 1 ? files[0] : files);
                      }
                      return true;
                    } catch (error) {
                      return false;
                    }
                  }}
                  filesLimit={filesLimit}
                  initialFiles={initialFiles}
                  inputProps={{
                    name: name,
                  }}
                  onDelete={(_, deletedIndex) => {
                    const formFiles = getValues(name) as Array<unknown>;
                    const nextValue =
                      filesLimit === 1
                        ? null
                        : formFiles.filter(
                            (_, fileIndex) => deletedIndex !== fileIndex,
                          );
                    field.onChange(nextValue);
                  }}
                  resourceReader={resourceReader}
                  onAlert={(message, type) => {
                    notify.warning(message, { type });
                  }}
                />
              </div>
            );
          return (
            <div>
              {initialFiles.length > 0 ? (
                <PreviewList
                  fileObjects={initialFiles}
                  resourceReader={resourceReader}
                />
              ) : (
                '-'
              )}
            </div>
          );
        }}
      />

      {errorMessage ? (
        <FormFeedback invalid>{translate(errorMessage)}</FormFeedback>
      ) : helperText ? (
        <FormFeedback>{helperText}</FormFeedback>
      ) : null}
    </FormGroup>
  );
};

export default React.memo(DropzoneAreaField) as typeof DropzoneAreaField;
